import { IntlShape } from 'react-intl';
import { WaitCauseCode, WaitCauseDTO } from '../../dto/ServiceStatisticsDTO';
import { formatDecimal, formatInt, formatMinutes, formatPercent } from './statisticsUtils';

/** Cuántas posibles causas se nombran por fila: las primeras, que el backend ya manda de la más a la menos relevante. */
export const MAX_CAUSES_SHOWN = 3;

/** Texto de cada causa en `es.json`/`en.json`. Los porcentajes de los textos nuevos llevan el «%» en el mensaje. */
const CAUSE_MESSAGE_ID: Record<WaitCauseCode, string> = {
    SHORT_SLOT: 'ServicesStatisticsCauseShortSlot',
    LATE_FINISH: 'ServicesStatisticsCauseOverrun',
    OVERBOOKED: 'ServicesStatisticsCauseOverbooked',
    HIGH_OCCUPANCY: 'ServicesStatisticsCauseOccupancyHigh',
    LOW_OCCUPANCY: 'ServicesStatisticsCauseOccupancyLow',
    LOW_ATTENDANCE: 'ServicesStatisticsCauseLowAttendance',
    ATTENTIONS_DROP: 'ServicesStatisticsCauseAttentionsDrop',
    WAIT_UP: 'ServicesStatisticsCauseWaitUp',
    MORE_PATIENTS: 'ServicesStatisticsCauseMorePatients',
    HIGHER_SEVERITY: 'ServicesStatisticsCauseHigherSeverity',
    LONGER_CONSULTS: 'ServicesStatisticsCauseLongerConsults',
    PEAK_HOURS: 'ServicesStatisticsCausePeakHours',
    PRIORITY_WAIT: 'ServicesStatisticsCausePriorityWait',
};

/** Un decimal solo si hace falta: «12 %» y «4,3 %». */
const pointsFormat = new Intl.NumberFormat('es-AR', { maximumFractionDigits: 1 });
const formatPoints = (value: number): string => pointsFormat.format(value);

const numberParam = (cause: WaitCauseDTO, key: string): number => {
    const value = Number(cause.params[key]);
    return Number.isFinite(value) ? value : 0;
};

/** «URGENCIA» → «Urgencia»: los códigos de atención se cargan en mayúsculas y en una frase se leen mal. */
function readableName(name: string): string {
    return name === name.toUpperCase() ? name.charAt(0).toUpperCase() + name.slice(1).toLowerCase() : name;
}

/** Los códigos que piden atención inmediata: una espera larga ahí es lo primero que hay que revisar. */
const CRITICAL_PRIORITIES = ['inmediato', 'emergencia'];

const normalize = (text: string): string => text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

/** El código de atención es de los que piden atención inmediata (Inmediato o Emergencia). */
export const isCriticalPriorityName = (name: string): boolean => CRITICAL_PRIORITIES.includes(normalize(name));

/** La causa es la espera por encima del objetivo de un código de Inmediato o Emergencia. */
export const isCriticalPriorityCause = (cause: WaitCauseDTO): boolean =>
    cause.code === 'PRIORITY_WAIT' && isCriticalPriorityName(String(cause.params.priorityName ?? ''));

/**
 * «Llegaron más pacientes» tiene dos textos, como en el PDF: con aumento contra el período anterior (el backend manda
 * `changePct`) o, si no, solo con los días pico. Las demás causas tienen uno solo.
 */
function messageIdOf(cause: WaitCauseDTO): string {
    const hasIncrease = cause.params.changePct !== undefined && cause.params.changePct !== null;
    return cause.code === 'MORE_PATIENTS' && !hasIncrease
        ? 'ServicesStatisticsCauseMorePatientsPeaks' : CAUSE_MESSAGE_ID[cause.code];
}

/** La causa escrita en palabras, con el número que importa y la primera letra en mayúscula. */
export function causeText(intl: IntlShape, cause: WaitCauseDTO): string {
    const n = (key: string) => numberParam(cause, key);
    const values: Record<string, string | number> = (() => {
        switch (cause.code) {
            case 'SHORT_SLOT':
                return { slot: formatInt(n('slot')), duration: formatMinutes(n('duration')) };
            case 'LATE_FINISH':
                return { days: formatInt(n('overrunDays')), scheduleDays: formatInt(n('scheduleDays')) };
            case 'OVERBOOKED':
            case 'HIGH_OCCUPANCY':
            case 'LOW_OCCUPANCY':
                return { ratio: formatPercent(n('pct') / 100) };
            case 'LOW_ATTENDANCE':
                // Enteros, como el PDF: el porcentaje va redondeado
                return { missed: formatInt(n('missed')), reserved: n('reserved'), pct: formatInt(n('pct')) };
            case 'ATTENTIONS_DROP':
            case 'WAIT_UP':
                return { change: formatPercent(n('pct') / 100) };
            case 'MORE_PATIENTS':
                // Enteros, como el PDF (el backend redondea estos números al escribir la causa)
                return {
                    avgPerDay: formatInt(n('avgPerDay')), changePct: formatInt(n('changePct')),
                    peakDays: n('peakDays'), days: n('days'),
                };
            case 'HIGHER_SEVERITY':
                return {
                    previousHighShare: formatPoints(n('previousHighShare')), highShare: formatPoints(n('highShare')),
                    highCount: n('highCount'),
                };
            case 'LONGER_CONSULTS':
                return { duration: formatDecimal(n('duration')), changePct: formatPoints(n('changePct')) };
            case 'PEAK_HOURS':
                return { fromHour: formatInt(n('fromHour')), toHour: formatInt(n('toHour')), sharePct: formatPoints(n('sharePct')) };
            case 'PRIORITY_WAIT':
                return {
                    priorityName: readableName(String(cause.params.priorityName ?? '')), avgWait: formatDecimal(n('avgWait')),
                    targetMinutes: formatInt(n('targetMinutes')), count: n('count'),
                };
        }
    })();
    const text = intl.formatMessage({ id: messageIdOf(cause) }, values);
    return text.charAt(0).toUpperCase() + text.slice(1);
}
